import React from 'react';
import styles from './Dashboard.module.css';
import { DataProviderInstance } from '../../api/DataProvider';
import Loading from '../../components/loading/Loading';
import useDashboardData from './hooks/useDashboardData';
import TodayHero from './components/TodayHero/TodayHero';
import FirstStepsCard from './components/FirstStepsCard/FirstStepsCard';
import ActionCenter from './components/ActionCenter/ActionCenter';
import RelationshipPanel from './components/Relationship/RelationshipPanel';
import DayRoomsCard from './components/DayRooms/DayRoomsCard';
import InsurancePendingCard from './components/InsurancePending/InsurancePendingCard';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import DemoDataNote from '../../components/demo-data/DemoDataNote';
import useAgendaView from './hooks/useAgendaView';
import AgendaViewToggle from './components/shared/AgendaViewToggle';

/**
 * Início: o dia da clínica num olhar (topo), o que precisa ser resolvido
 * agora (coluna principal), os convênios a conferir, as salas do dia e os contatos
 * de relacionamento (lateral).
 */
function Dashboard() {
  const { session, CompanyConfig } = DataProviderInstance();
  const { hasOwnScheduleScope } = useRoleAccess();
  // Salas do dia: só faz sentido com salas cadastradas e para quem vê todas as agendas
  const showDayRooms = (CompanyConfig?.Rooms || []).length > 0 && !hasOwnScheduleScope();
  const { isLoaded, dashboardData, todayAppointments, updateStepChecks, toConfirm } = useDashboardData(session?.currentCompany);
  // "Minha agenda / Todas": em conta com várias agendas, cada profissional abre no próprio dia
  const { view, setView, showFilter, isMine, ownResourceId, filterList } = useAgendaView();

  if (!isLoaded) {
    return (
      <div className="page-content">
        <Loading />
      </div>
    );
  }

  return (
    <div className="page-content">
      <div className={styles.container}>
        <div className={styles.dashboard}>
          <TodayHero
            appointments={filterList(todayAppointments)}
            onStepChecksChange={updateStepChecks}
            isMine={isMine}
            ownResourceId={ownResourceId}
            viewToggle={showFilter ? <AgendaViewToggle view={view} onChange={setView} /> : null}
          />

          {/* Conta de teste: os números acima incluem os dados de exemplo */}
          <DemoDataNote />

          {/* Primeiros passos de quem acabou de chegar (itens conforme o papel) */}
          <FirstStepsCard />

          <div className={styles.columns}>
            <div className={styles.main}>
              <ActionCenter
                toConfirm={filterList(toConfirm)}
                pendingRequests={filterList(dashboardData?.PendingPatientRequests?.items)}
                reservations={filterList(dashboardData?.PendingReservations)}
                canceled={filterList(dashboardData?.CanceledAppointments)}
              />
              {/* Some sozinho quando a empresa não tem operadora de convênio */}
              <InsurancePendingCard />
            </div>
            <div className={styles.side}>
              {showDayRooms && <DayRoomsCard resourceId={isMine ? ownResourceId : null} />}
              <RelationshipPanel
                birthdays={dashboardData?.PatientsBirthdays || []}
                firstVisits={dashboardData?.FirstVisitFollowUp || []}
                npsFeedback={dashboardData?.NPSFeedback || []}
                googleReviews={dashboardData?.GoogleReviewSuggestions || []}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
